import React, { useEffect, useState } from "react"
import { api } from "../../lib/api"
import { formatScore } from "../../lib/format"
import { Card } from "../ui/Card"
import { ScoreTrend } from "../ui/ScoreTrend"
import { SubjectBars } from "../ui/SubjectBars"
import type { Performance } from "../../types"

/**
 * Boletim de desempenho no dashboard. Duas metades que se complementam:
 * (1) "Seu progresso" — números de conquista + evolução da nota (motivar);
 * (2) "Onde você pode melhorar" — matérias fracas pior-primeiro (direcionar).
 * Não renderiza nada enquanto carrega ou sem simulados concluídos.
 */
export default function Insights() {
  const [perf, setPerf] = useState<Performance | null | undefined>(undefined)

  useEffect(() => {
    api
      .get<Performance>("/api/me/performance")
      .then(setPerf)
      .catch(() => setPerf(null))
  }, [])

  if (perf === undefined || !perf || perf.total === 0) return null

  const answered = perf.by_subject.filter((s) => s.total > 0)
  const sorted = [...answered].sort((a, b) => a.accuracy - b.accuracy)
  const weak = sorted.filter((s) => s.accuracy < 50)
  const totalCorrect = answered.reduce((sum, s) => sum + s.correct, 0)
  const totalQuestions = answered.reduce((sum, s) => sum + s.total, 0)
  const overall = totalQuestions === 0 ? 0 : Math.round((100 * totalCorrect) / totalQuestions)

  // Sem questões corrigidas não há o que analisar (evita "Bom nível: 0%")
  if (totalQuestions === 0) return null

  // Evolução: nota mais recente − mais antiga da série (recent[0] é a mais antiga).
  const trend = perf.recent
  const delta = trend.length >= 2 ? trend[trend.length - 1].score - trend[0].score : null

  const focus = weak.slice(0, 3).map((s) => s.subject)
  const callout =
    weak.length > 0
      ? `Você acerta ${overall}% no geral. Dá pra subir bastante focando em ${listing(focus)} (abaixo de 50%).`
      : `Bom nível: ${overall}% de acerto. Siga treinando pra manter o ritmo e subir a nota.`

  return (
    <>
      {/* ===== Seu progresso — conquista + trajetória ===== */}
      <section aria-label="Seu progresso">
        <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wider text-navy-500">Seu progresso</h2>

        <Card className="mb-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4">
            <StatTile label={perf.total === 1 ? "simulado feito" : "simulados feitos"} value={String(perf.total)} />
            <StatTile
              label="melhor nota"
              value={perf.best_score != null ? formatScore(perf.best_score) : "—"}
              accent="gold"
            />
            <StatTile label="média geral" value={perf.average_score != null ? formatScore(perf.average_score) : "—"} />
            <StatTile
              label="evolução"
              value={delta == null ? "—" : `${delta >= 0 ? "+" : "−"}${formatScore(Math.abs(delta))}`}
              sub={delta == null ? "faça +1 simulado" : "desde o 1º"}
              accent={delta == null ? "navy" : delta > 0.05 ? "emerald" : delta < -0.05 ? "red" : "navy"}
            />
          </div>
        </Card>

        {trend.length >= 2 && (
          <Card>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-navy-500">Evolução da nota</p>
            <p className="mb-2 text-[11px] text-navy-400">Seus últimos {trend.length} simulados</p>
            <ScoreTrend points={trend} />
          </Card>
        )}
      </section>

      {/* ===== Seu diagnóstico — onde a pessoa perde ponto ===== */}
      <section aria-label="Seu diagnóstico">
        <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wider text-navy-500">Seu diagnóstico</h2>

        <Card highlighted className="mb-3 flex items-start gap-3">
          <span
            className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${weak.length > 0 ? "bg-red-500" : "bg-emerald-500"}`}
          />
          <p className="text-sm text-navy-800">{callout}</p>
        </Card>

        <Card>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-navy-500">Aproveitamento por matéria</p>
          <SubjectBars subjects={sorted} />
        </Card>
      </section>
    </>
  )
}

type Accent = "navy" | "gold" | "emerald" | "red"

const ACCENT: Record<Accent, string> = {
  navy: "text-navy-900",
  gold: "text-gold-600",
  emerald: "text-emerald-600",
  red: "text-red-600",
}

/** Número de conquista em destaque (display font, à moda do cronômetro do simulado). */
function StatTile({ label, value, sub, accent = "navy" }: { label: string; value: string; sub?: string; accent?: Accent }) {
  return (
    <div className="flex flex-col">
      <span className={`font-display text-4xl font-bold leading-none tabular-nums ${ACCENT[accent]}`}>{value}</span>
      <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-wider text-navy-500">{label}</span>
      {sub && <span className="mt-0.5 text-[11px] text-navy-400">{sub}</span>}
    </div>
  )
}

function listing(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ""
  return `${items.slice(0, -1).join(", ")} e ${items[items.length - 1]}`
}
